import { startCase } from 'lodash-es';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import {
  ChangeGroup,
  ChangeRole,
  Container,
  ContainerContent,
  ContainerHeader,
} from '@/component';
import UserInviteFormWithTrigger from '@/component/User/UserInviteFormWithTrigger';
import UserGroupFormWithTrigger from '@/component/UserGroup/UserGroupCreateForm';
import { USER_ROLE } from '@/constant/userConstant';
import {
  useUserCountQuery,
  useUserGroupListQuery,
  useUserGroupUserListQuery,
  useUserListQuery,
  useUserRemoveQuery,
  useUserRoleMapper,
} from '@/hook';
import usePaginationSearchParams from '@/hook/tanstackQuery/usePaginationSearchParams';
import type { VTableColumnFilter } from '@/package';
import {
  AccountCircle,
  CheckBox,
  CheckBoxOutlineBlank,
  Close,
  Remove,
  VDrawer,
  VFlex,
  VInput,
  VListItem,
  VModal,
  VPagination,
  VStack,
  VTab,
  VTabTrigger,
  VTabTriggerContainer,
  VTable,
  useVModal,
} from '@/package';
import { VIconButton } from '@/package/kiska/src/component/UI/VIconButton';
import { useMainLayout } from '@/provider';
import { useAuthStore } from '@/store';
import type { User, UserGroup, UserRole } from '@/type';
import { convertUserEmailToHexColor } from '@/util';

const MemberListPage = () => {
  const [isMemberProfileOpen, setIsMemberProfileOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<Nullish<User>>(null);
  const [selectedGroupUserList, setSelectedGroupUserList] = useState<User[]>(
    [],
  );
  const [selectedGroupID, setSelectedGroupID] =
    useState<Nullish<UserGroup['id']>>(null);
  const [isUserRemoving, setIsUserRemoving] = useState(false);
  const isUserGroupSelected = !!selectedGroupID;
  const { data: userList, isPending: isUserPending } = useUserListQuery({
    enabled: !isUserGroupSelected,
  });
  const { data: userCount } = useUserCountQuery();
  const { data: groupUserList, isPending: isGroupUserPending } =
    useUserGroupUserListQuery({
      enabled: isUserGroupSelected,
      userGroupID: selectedGroupID ?? 0,
    });
  const { data: groupList, isPending: isGroupPending } =
    useUserGroupListQuery();

  const { mutateAsync: removeUser } = useUserRemoveQuery();
  const isRemovable = !!selectedGroupUserList.length;
  const { user: me } = useAuthStore();
  const isRemovableRole = me.role === 'Admin' || me.role === 'SiteAdmin';
  const navigate = useNavigate();
  const { setTitle } = useMainLayout();
  const { mapUserRole } = useUserRoleMapper();

  const userNum = userList?.reduce(
    (acc, cur) => {
      return {
        ...acc,
        [cur.role]: acc[cur.role] + 1,
      };
    },
    {
      SiteAdmin: 0,
      Manager: 0,
      Worker: 0,
      Admin: 0,
      Guest: 0,
    },
  ) ?? {
    SiteAdmin: 0,
    Manager: 0,
    Worker: 0,
    Admin: 0,
    Guest: 0,
  };
  const numUserRoleList: { label: string; role: UserRole; num: number }[] = [
    {
      label: mapUserRole('SiteAdmin'),
      role: 'SiteAdmin',
      num: userNum.SiteAdmin,
    },
    {
      label: mapUserRole('Manager'),
      role: 'Manager',
      num: userNum.Manager,
    },
    {
      label: mapUserRole('Worker'),
      role: 'Worker',
      num: userNum.Worker,
    },
    {
      label: mapUserRole('Guest'),
      role: 'Guest',
      num: userNum.Guest,
    },
  ];

  const totaluser = numUserRoleList.reduce((acc, cur) => acc + cur.num, 0);

  const handleRemoveUserList = async () => {
    await Promise.allSettled(selectedGroupUserList.map(u => removeUser(u.id)));
  };

  const handleSelectGroup = (groupID: UserGroup['id']) => {
    setSelectedGroupID(groupID);
  };

  useEffect(() => {
    if (!selectedUser) return;

    const newUser = userList?.find(u => u.id === selectedUser.id);

    setSelectedUser(newUser);
  }, [userList]);

  useEffect(() => {
    setTitle('Members');
  }, []);

  if (
    isGroupPending || isUserGroupSelected ? isGroupUserPending : isUserPending
  )
    return <></>;

  return (
    <>
      <Container>
        <ContainerHeader>
          <VStack className="w-full pt-6 pb-2" gap={0.5}>
            {(me.role === 'SiteAdmin' || me.role === 'Admin') && (
              <div className="w-fit">
                <VTab
                  defaultValue="0"
                  onValueChange={val => {
                    if (val === '1') navigate('/admin-setting');
                  }}
                >
                  <VTabTriggerContainer>
                    <VTabTrigger value="0">Members</VTabTrigger>
                    <VTabTrigger value="1">Admin settings</VTabTrigger>
                  </VTabTriggerContainer>
                </VTab>
              </div>
            )}
            <VFlex gap={1} items="center" className="w-full">
              <div className="text-headline-md font-semibold text-nowrap">
                All members
              </div>
              <VFlex justify="between" className="w-full" items="center">
                <VFlex className="px-2" gap={0.5} items="center">
                  <VFlex className="text-headline-sm text-blue-300" gap={0.25}>
                    <span>Total</span>
                    <span className="font-bold">{totaluser}</span>
                  </VFlex>
                  {numUserRoleList.map(({ label, role, num }) => {
                    return (
                      <VFlex
                        gap={0.5}
                        items="center"
                        key={role}
                        className="text-gray-500"
                      >
                        <div className="h-4 w-px bg-gray-300" />
                        <VFlex
                          className="text-headline-sm"
                          gap={0.25}
                          key={role}
                        >
                          <span>{label}</span>
                          <span className="font-bold">{num}</span>
                        </VFlex>
                      </VFlex>
                    );
                  })}
                </VFlex>
                <VFlex gap={0.5} items="center">
                  <UserGroupFormWithTrigger
                    key={Math.random()}
                    editOrCreate="create"
                    userGroupList={groupList ?? []}
                    userList={userList ?? []}
                  />
                  <UserInviteFormWithTrigger />
                  {isRemovableRole && (
                    <VIconButton
                      Icon={Remove}
                      disabled={!isRemovable}
                      size="lg"
                      variant="danger"
                      onClick={() => setIsUserRemoving(true)}
                    />
                  )}
                </VFlex>
              </VFlex>
            </VFlex>
          </VStack>
        </ContainerHeader>

        <ContainerContent>
          {groupList && (
            <UserGroupList
              groupList={groupList}
              userCount={userCount ?? 0}
              userList={
                isUserGroupSelected ? (groupUserList ?? []) : (userList ?? [])
              }
              openMemberProfile={() => setIsMemberProfileOpen(true)}
              setSelectedUser={setSelectedUser}
              selectedGroupUserList={selectedGroupUserList}
              setSelectedGroupUserList={setSelectedGroupUserList}
              onSelectGroup={handleSelectGroup}
            />
          )}
        </ContainerContent>
      </Container>

      <VDrawer
        dialogMainClass={'!bg-white'}
        position="right"
        size="xs"
        visible={isMemberProfileOpen}
        onUpdateVisible={setIsMemberProfileOpen}
      >
        <UserProfile
          closeMemberProfile={() => setIsMemberProfileOpen(false)}
          groupList={groupList}
          selectedUser={selectedUser}
        />
      </VDrawer>

      <VModal
        visible={isUserRemoving}
        title={() => (
          <div className="translate-y-1 text-body-xl text-error-500">
            Delete Project
          </div>
        )}
        size="small"
        footer={{
          cancelText: 'Cancel',
          confirmText: 'Delete',
          onConfirm: async () => {
            await handleRemoveUserList();
            setIsUserRemoving(false);
          },
        }}
        onUpdateVisible={visible => setIsUserRemoving(visible)}
      >
        <VFlex direction="col" className="gap-[10px]">
          <div className="text-body-xl text-black">
            Are you sure you want to remove the selected member?
          </div>

          <div className="w-full text-gray-500">
            This member will no longer have access to the workspace and all
            related permissions will be revoked.
            <br /> You can invite them again at any time.
          </div>
        </VFlex>
      </VModal>
    </>
  );
};

const UserGroupList = ({
  userCount,
  userList,
  groupList,
  openMemberProfile,
  setSelectedUser,
  selectedGroupUserList,
  setSelectedGroupUserList,
  onSelectGroup,
}: {
  userCount: number;
  userList: User[];
  groupList: UserGroup[];
  setSelectedUser: (user: User) => void;
  openMemberProfile: () => void;
  selectedGroupUserList: User[];
  setSelectedGroupUserList: Dispatch<SetStateAction<User[]>>;
  onSelectGroup?: (groupID: UserGroup['id']) => void;
}) => {
  const { mapUserRole } = useUserRoleMapper();
  const { limit, offset, setOffset } = usePaginationSearchParams('user');
  const { data: totalUserCount } = useUserCountQuery();
  const allUserGroup: UserGroup = {
    createdAt: '-',
    deletedAt: '-',
    id: 0,
    name: 'All member',
    updatedAt: '-',
    users: Array.from({ length: userCount }),
  };
  const [selectedGroup, setSelectedGroup] = useState<UserGroup>(allUserGroup);
  const { mutate: removeUser } = useUserRemoveQuery();
  const { openDangerConfirm } = useVModal();
  const { user: me } = useAuthStore();
  const [filteredUserRoleList, setFilteredUserRoleList] = useState<UserRole[]>(
    [],
  );
  const [searchTerm, setSearchTerm] = useState('');

  const isAllUserRoleSelected =
    filteredUserRoleList.length === Object.values(USER_ROLE).length ||
    filteredUserRoleList.length === 0;

  const filteredUserList = (() => {
    const roleFilteredUserList = isAllUserRoleSelected
      ? userList
      : userList.filter(user => filteredUserRoleList.includes(user.role));

    const searchTermFilteredUserList = roleFilteredUserList.filter(
      user =>
        user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    return searchTermFilteredUserList;
  })();

  const columnFilter = [
    {
      id: 'role',
      itemList: Object.values(USER_ROLE)
        .filter(role => role !== 'Admin')
        .map(role => ({
          value: role,
          label: mapUserRole(role),
          isSelected: filteredUserRoleList.includes(role),
        })),
    },
  ];
  const isUserRemovable = !(
    me.role === 'Manager' ||
    me.role === 'Worker' ||
    me.role === 'Guest'
  );

  const isAllSelected =
    selectedGroup.users.length &&
    selectedGroup.users.every(u =>
      selectedGroupUserList.find(gu => gu.id === u.id),
    );

  const onHeaderCheckboxClick = () => {
    isAllSelected
      ? setSelectedGroupUserList([])
      : setSelectedGroupUserList([...(selectedGroup.users ?? [])]);
  };

  const onUserCheckboxClick = (user: User) => {
    const idx = selectedGroupUserList.findIndex(u => u.id === user.id);
    if (idx === -1) {
      setSelectedGroupUserList([...selectedGroupUserList, user]);
    } else {
      setSelectedGroupUserList(
        selectedGroupUserList.filter((_, i) => i !== idx),
      );
    }
  };

  const onUserRemoveButtonClick = (userID: User['id']) => {
    if (!isUserRemovable) return;
    openDangerConfirm({
      title: 'Remove member',
      size: 'small',
      onConfirm: () => removeUser(userID),
      message: () => (
        <VStack className="" gap={0.25}>
          <div className="text-body-xl text-black">
            Are you sure you want to remove the selected member?
          </div>
          <div className="text-body-lg text-gray-600">
            This member will no longer have access to the workspace and all
            related permissions will be revoked.
            <br /> You can invite them again at any time.
          </div>
        </VStack>
      ),
    });
  };

  const renderedUserList = useMemo(() => {
    return (
      filteredUserList.map(user => {
        const name = user.name || user.email.split('@')[0];
        const email = user.email;
        const renderedUser = {
          ...user,
          id: `${user.id}`,
          checked: !!selectedGroupUserList.find(u => u.id === user.id),
          name: { name, email },
          role: user.role,
          lastWorked: null,
          remove: null,

          user: user,
        };
        return renderedUser;
      }) ?? []
    );
  }, [filteredUserList, selectedGroupUserList]);

  useEffect(() => {
    setSelectedGroupUserList([]);
  }, [selectedGroup]);

  return (
    <VFlex className="h-full flex-1  py-8" gap={1}>
      <div className="h-full w-[324px] min-w-[324px] overflow-y-auto rounded-md border border-skyBlue-200">
        <VListItem<UserGroup & { len?: number }>
          list={[allUserGroup, ...(groupList ?? [])].map(group => ({
            ...group,
            len: group.users.length,
          }))}
          value={selectedGroup}
          onChange={group => {
            setSelectedGroup(group);
            onSelectGroup?.(group.id);
            setOffset(0);
          }}
        />
      </div>
      <VStack className="h-full w-full">
        <VFlex className="w-full gap-2" justify="between">
          <VFlex items="center" gap={0.5}>
            {selectedGroup.id ? (
              <UserGroupFormWithTrigger
                key={Math.random()}
                editOrCreate="edit"
                userGroupList={groupList ?? []}
                userList={userList ?? []}
                editingGroup={selectedGroup}
              />
            ) : (
              <div className="min-w-1"></div>
            )}

            {!!selectedGroupUserList.length && (
              <div className="text-body-xl text-gray-500">
                {selectedGroupUserList.length} selected
              </div>
            )}
          </VFlex>
          <VInput
            type="search"
            placeholder="Search"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </VFlex>
        <VStack className="flex-1 overflow-hidden ">
          <div className="flex-1 overflow-y-auto ">
            <VTable
              data={renderedUserList}
              columnList={['checked', 'name', 'role', 'remove']}
              columnStyleMap={{
                checked: { className: isUserRemovable ? 'w-9' : 'hidden' },
                name: { className: 'w-full' },
                role: { className: 'w-[174px]' },
                lastWorked: { className: 'w-[174px]' },
                remove: { className: isUserRemovable ? 'w-[80px]' : 'hidden' },
              }}
              onRowClick={e => {
                setSelectedUser(e._target.row.user);
                openMemberProfile();
              }}
              columnRenderer={{
                checked: () => {
                  const Icon = isAllSelected ? CheckBox : CheckBoxOutlineBlank;
                  return (
                    <Icon
                      className="h-5 w-5"
                      role="button"
                      onClick={onHeaderCheckboxClick}
                    />
                  );
                },
                name: startCase,
                remove: startCase,
                role: startCase,
                lastWorked: startCase,
              }}
              cellRenderer={{
                checked: (value, _, row) => {
                  const Icon = value ? CheckBox : CheckBoxOutlineBlank;

                  return (
                    <Icon
                      className="h-5 w-5"
                      role="button"
                      onClick={e => {
                        e.stopPropagation();
                        onUserCheckboxClick(row.user);
                      }}
                    />
                  );
                },
                name: (value, _, row) => (
                  <VFlex gap={0.5} items="center" role="button">
                    <VFlex
                      justify="center"
                      items="center"
                      className="h-5 w-5 rounded-full"
                      style={{
                        backgroundColor: convertUserEmailToHexColor(
                          row.user.email,
                        ).backgroundColor,
                        color: convertUserEmailToHexColor(row.user.email).color,
                      }}
                    >
                      {value.name[0]}
                    </VFlex>
                    <div className="text-body-xl text-black" role="button">
                      {value.name}
                    </div>
                    <div className="h-3 w-px bg-gray-300" />
                    <div className="text-body-xl text-gray-400">
                      {value.email}
                    </div>
                  </VFlex>
                ),
                role: value => startCase(mapUserRole(value)),
                remove: (_1, _2, row) => {
                  const disabled =
                    row.role === 'SiteAdmin' ||
                    row.role === 'Admin' ||
                    row.role === 'Guest';
                  return (
                    <VFlex justify="center" items="center" className="w-full">
                      <VIconButton
                        Icon={Remove}
                        size="md"
                        variant="roundDanger"
                        disabled={disabled}
                        onClick={e => {
                          e.stopPropagation();
                          onUserRemoveButtonClick(row.id);
                        }}
                      />
                    </VFlex>
                  );
                },
                lastWorked: () => '-',
              }}
              columnFilter={
                columnFilter as VTableColumnFilter<
                  (typeof renderedUserList)[number]
                >[]
              }
              onColumnFilterChange={e => {
                const roleColumnFilter = e.find(filter => filter.id === 'role');
                const roleColumnFilterItemList =
                  roleColumnFilter?.itemList.filter(item => item.isSelected);
                setFilteredUserRoleList(
                  roleColumnFilterItemList?.map(item => item.value) ?? [],
                );
              }}
            />
          </div>

          {selectedGroup.id === 0 && (
            <VFlex justify="center" items="center">
              <VPagination
                currentPage={offset / limit + 1}
                totalPage={Math.ceil((totalUserCount ?? 0) / limit)}
                onChange={page => setOffset((page - 1) * limit)}
              />
            </VFlex>
          )}
        </VStack>
      </VStack>
    </VFlex>
  );
};

UserGroupList.displayName = 'UserGroupList';

const UserProfile = ({
  closeMemberProfile,
  groupList,
  selectedUser,
}: {
  closeMemberProfile: () => void;
  groupList?: UserGroup[];
  selectedUser: Nullish<User>;
}) => {
  const { t } = useTranslation();
  const { user: me } = useAuthStore();
  const isUserEditable =
    !(me.role === 'Manager' || me.role === 'Worker' || me.role === 'Guest') &&
    selectedUser?.role !== 'Guest';
  return (
    <VStack className="gap-[40px] px-8 py-6">
      <VFlex justify="between" className="w-full" items="center">
        <div className="text-headline-md">
          {t('user.memberProfile.cacotheline')}
        </div>
        <Close
          role="button"
          className="h-6 w-6 text-gray-400"
          onClick={closeMemberProfile}
        />
      </VFlex>
      <VFlex gap={1.5} className="w-full">
        <AccountCircle className="h-[184px] w-[184px]" />
        <VStack className="flex-1" gap={2.5}>
          <VStack className="flex-1" gap={1}>
            <VInput
              label="Name"
              value={selectedUser?.name || selectedUser?.email.split('@')[0]}
              readOnly={isUserEditable}
            />
            <VInput
              label="Email"
              value={selectedUser?.email ?? ''}
              readOnly={isUserEditable}
            />
            {groupList && (
              <ChangeRole
                selectedUser={selectedUser!}
                isUserEditable={isUserEditable}
              />
            )}
            {groupList && (
              <ChangeGroup
                groupList={groupList}
                selectedUser={selectedUser!}
                isUserEditable={isUserEditable}
              />
            )}
          </VStack>
        </VStack>
      </VFlex>
    </VStack>
  );
};

UserProfile.displayName = 'UserProfile';

export default MemberListPage;
